import { IReproxyLink } from "@services/getMinerList";
import {
  Dispatch,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import minerReducer, { MinerActionData, MinerActions } from "./minerReducer";
import { ALGOS_E } from "@constants/Algos";
import { MINER_STATUSES_E } from "@constants/MinerStatuses";
import { IContractValue } from "@components/global/new/ContractSelector/ContractSelector.props";
import { useContracts } from "@utils/hooks/useContracts";
import { dev } from "@utils/dev";
import { useDispatch } from "react-redux";
import { refreshMiner } from "@store/storedMiners/actions";
import { MinerIdEvent, UserSettingEvent } from "@services/http/socket/responses";
import { IAdminMinerInfo, IAdminMinerTable } from "@services/getMinerInfo";

export interface MinerContextProps {
  miner: IAdminMinerTable;
  minerContract?: IContractValue;
  dispatchMiner: Dispatch<MinerActionData>;
  updateMiner: (info: IAdminMinerInfo) => void;
  updateProxyLink: (port: number) => void;
  updateMinerAllSettings: (settings: UserSettingEvent) => void;
  removeUserSettings: (userId: number) => void;
}

// interface UserSettingsContextProps {
//   userSettings: IStoredSetting[];
//   getUserSettings(): void;
// }

export const MinerContext = createContext<MinerContextProps>({
  miner: {
    id: 0,
    miner_id: "",
    profile: {
      id: 0,
      name: "Unassigned",
      consumption: -1, // чтобы выловить этот случай
      performance: -1,
      hash: ALGOS_E.SHA256,
      meta: {},
    },
    tab: "all",
    hash: ALGOS_E.SCRIPT,
    speed_default: 0,
    speed_current: 0,
    speed_drop: 0,
    setting: "",
    status: MINER_STATUSES_E.OFF,
    location: "",
    location_id: "",
    notifications: [],
    admin_note: "",
    user_note: "",
    sync_note: "",
    proof: 0,
    settingid: 0,
    plan: {
      id: 3,
      name: "default",
      meta: "",
      schedule: [],
    },
    chat: [],
    is_own_mining: true,
    all_settings: [],
  },
  dispatchMiner: () => { },
  updateProxyLink: () => { },
  updateMiner: () => { },
  updateMinerAllSettings: () => { },
  removeUserSettings: () => { },
});

interface IMinerContextProps {
  miner: IAdminMinerTable;
  children: ReactNode;
}

const MinerContextProvider = ({ miner, children }: IMinerContextProps) => {
  const contracts = useContracts();
  const [state, dispatchMiner] = useReducer(minerReducer, miner);
  const dispatch = useDispatch();
  const [minerContract, setMinerContract] = useState<
    IContractValue | undefined
  >();

  useEffect(() => {
    onSetMinerContract();
  }, [state]);

  const onSetMinerContract = () => {
    if (minerContract?.id === miner.contract_id) return;
    const crCon = contracts.find((c) => c.id === miner.contract_id);
    setMinerContract(crCon);
  };

  const updateProxyLink = (port: number) => {
    const link: IReproxyLink = {
      Port: port,
      Target: "",
      LastActive: "",
    };
    dispatchMiner({
      type: MinerActions.ProxyLink,
      payload: link,
    });
  };

  const updateMinerAllSettings = (
    settings: UserSettingEvent
  ) => {
    dispatchMiner({
      type: MinerActions.AllSettingsAdd,
      payload: settings,
    });
  };

  const removeUserSettings = (userId: number) => {
    dispatchMiner({
      type: MinerActions.AllSettingsRemoveByUserId,
      payload: userId,
    });
  }

  function updateMiner(info: IAdminMinerInfo) {
    // взято из reducer-а
    const um = { ...state }; // as IAdminMinerTable;
    if (info.location?.name) miner.location = info.location.name;
    um.admin_notes = info.admin_notes;
    um.location_raw = info.location;
    um.notifications = info.notifications;
    um.plan = info.plan;
    um.smooth = info.smooth;
    um.user_note = info.user_note;
    um.meta = info.meta;
    um.photos = info.photos;
    um.chat = [...info.chat];

    um.customer_email = info.customer_email || state.customer_email;
    um.customer_id = info.customer_id || state.customer_id;
    um.customer_name = info.customer_name || state.customer_name;
    um.customer_uuid = info.customer_uuid || state.customer_uuid;

    um.admin_mining_proof = info.admin_mining_proof;
    um.admin_mining_setting = info.admin_mining_setting;
    um.admin_note = info.admin_note;
    um.proof = info.proof;
    um.sync_note = info.sync_note;
    um.contract_id = info.contract_id;
    um.contract_name = info.contract_name;
    um.installed = info.installed;

    um.all_settings = info.all_settings;

    dispatchMiner({
      type: MinerActions.Miner,
      payload: um, //{ ...miner, ...um },
    });
  }

  return (
    <MinerContext.Provider
      value={{
        miner: state,
        //userSettings,
        minerContract,
        updateProxyLink,
        updateMiner,
        updateMinerAllSettings,
        removeUserSettings,
        dispatchMiner: (value) => {
          dispatchMiner(value);
          if (value.type === MinerActions.MinerId) {
            dispatch(
              refreshMiner({
                ...state,
                miner_id: (value.payload as MinerIdEvent).miner_id,
              })
            );
          }
        },
        // getUserSettings,
      }}
    >
      {children}
    </MinerContext.Provider>
  );
};

export default MinerContextProvider;

export const useMinerContext = () => {
  return useContext(MinerContext);
};
